'use client'

import { PdpClientLink } from 'links/pdp/client'
import { ColorsAmountLabel } from 'product-card/components/shared/ProductColor/ColorsAmountLabel'
import { SizesAmountLabel } from 'product-card/components/shared/ProductSizes/SizesAmountLabel'
import { useProductCardConsumerLayoutContext } from 'product-card/hooks/useProductCardConsumerLayoutContext'
import { useProductCardContext } from 'product-card/hooks/useProductCardContext'
import { useProductCardStock } from 'product-card/hooks/useProductCardStock'
import { getProductLinkLabel } from 'product-card/utils/getProductLinkLabel/getProductLinkLabel'
import type { Product, ProductColor } from 'product/types'
import { useMatchMedia } from 'utils/matchMedia/hooks'

import { useShouldRenderColorLink } from '../../../hooks/useShouldRenderColorLink'

import styles from './PdpLink.module.scss'
import text from 'fukku/styles/texts.module.scss'
import mq from 'utils/matchMedia/styles/MatchMediaQueries.module.scss'

interface PdpLinkProps {
	product: Product
	productColors: ProductColor[]
	showAsFeatured: boolean
}

const COLOR_SIZE_DIVIDER = ' - '

export const PdpLink = ({
	product,
	productColors,
	showAsFeatured,
}: PdpLinkProps) => {
	const [isLargeOrGreater] = useMatchMedia([mq.islessthanlarge])
	const { selectedColorId, lookId } = useProductCardContext()
	const { hasProductRelatedModelSizes } = useProductCardStock({
		productColors,
	})
	const { isTwoColumns } = useProductCardConsumerLayoutContext()

	const haveColors = productColors?.length > 1

	const {
		shouldRenderColorLink,
		shouldRenderColorAmountLabel,
		shouldRenderColorSizesDivider,
	} = useShouldRenderColorLink({
		showAsFeatured,
		haveColors,
		hasProductRelatedModelSizes,
		isLargeOrGreater,
		isTwoColumns: isTwoColumns ?? false,
	})

	if (shouldRenderColorLink) {
		return (
			<PdpClientLink
				product={product}
				colorId={selectedColorId}
				lookId={lookId.current}
				linkProps={{
					className: `${text.bodyS} ${styles.colorLinkTitle}`,
					'data-testid': 'productCard.pdpLink',
					linkProps: {
						'aria-label': getProductLinkLabel(product, selectedColorId),
					},
				}}
			>
				{shouldRenderColorAmountLabel && (
					<ColorsAmountLabel productColors={productColors} />
				)}
				{shouldRenderColorSizesDivider && COLOR_SIZE_DIVIDER}
				{hasProductRelatedModelSizes && (
					<SizesAmountLabel productColors={productColors} />
				)}
			</PdpClientLink>
		)
	}

	return null
}
